/**
 * Pré-remplissage des blocs « Particulier » / « Professionnel » des modales
 * « Soumettre le CTVO » et « Soumettre le certificat de cession ».
 *
 * Règle : un bloc ne se pré-remplit QUE depuis une entité du même type.
 *
 * Sans elle, les deux blocs apparaissent renseignés en même temps. Une entité
 * `pro` porte à dessein l'identité du représentant figurant au Kbis à côté de
 * sa raison sociale (cf. backend utils/entityCompanyFields) : le bloc
 * « Particulier », monté avec `defaultValue={deal?.SellerLastName}`, affichait
 * donc le nom du gérant dès que l'exploitant basculait le radio — et il ne
 * pouvait pas l'effacer, la valeur revenant à chaque remontage du champ.
 * Retour François 2026-09-17 sur GP-482-JC : « PROCELL en particulier ET
 * PROCELL 89178470500013 en pro, je ne peux pas supprimer le particulier ».
 *
 * Le type de référence est celui ENREGISTRÉ (`deal.*Type`), pas celui coché :
 * basculer le radio pour corriger une entité mal typée doit ouvrir un bloc
 * VIDE, à saisir — pas recopier les valeurs de l'autre nature.
 */

/** `CG_Entities.EntityType` tel que renvoyé par l'API deals. */
export type StoredEntityType = string | null | undefined;

const isPro = (storedType: StoredEntityType): boolean => storedType === 'pro';

/** Champ d'identité personne physique : vide dès que l'entité est une société. */
export const personBlockDefault = (storedType: StoredEntityType, value: unknown): string =>
  isPro(storedType) ? '' : ((value as string | null | undefined) ?? '');

/** Champ société (raison sociale, SIRET) : vide dès que l'entité est un particulier. */
export const companyBlockDefault = (storedType: StoredEntityType, value: unknown): string =>
  isPro(storedType) ? ((value as string | null | undefined) ?? '') : '';

/**
 * Civilité du bloc particulier : `'male'` / `'female'` attendus par le Select.
 *
 * Renvoie `''` pour une société ET pour un particulier sans civilité connue.
 * Le ternaire historique `x === 'mr' ? 'male' : 'female'` affichait « Féminin »
 * pour toute valeur absente — c'est ce qu'on voyait sur le vendeur PROCELL.
 */
export const genderBlockDefault = (storedType: StoredEntityType, gender: unknown): string => {
  if (isPro(storedType)) return '';
  if (gender === 'mr') return 'male';
  if (gender === 'mme') return 'female';
  return '';
};
